import { useGenerateReportPdfMutation } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { PictureAsPdf } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useAtomValue } from 'jotai';

export const ExportPoiReportButton = () => {
  const selectedReport = useAtomValue(state.selectedReport);
  const [loading, setLoading] = useState(false);

  const [generateReportPdf] = useGenerateReportPdfMutation();
  const { showSnackBar } = useSnackBarMessage({ isReportDownload: true });

  const handlePointOfInterestPdf = async () => {
    if (!selectedReport?.id) {
      throw new Error('Report ID is missing');
    }

    const { data } = await generateReportPdf({
      variables: { reportIds: [selectedReport.id] },
    });

    if (!data?.generateReportPdf?.downloadUrl) {
      throw new Error('Failed to generate PDF');
    }

    showSnackBar('Your reports are ready. Download is starting...', 'success');

    // Download from URL
    if (data.generateReportPdf.downloadUrl && data.generateReportPdf.downloadUrl.length > 0) {
      const downloadUrl = data.generateReportPdf.downloadUrl.at(0);
      if (downloadUrl) {
        // Add a delay to ensure the second snackbar is visible before starting download
        setTimeout(() => {
          // Open the download URL directly - the backend should set the correct filename
          window.open(downloadUrl, '_blank');

          // Show final success message after download
          setTimeout(() => {
            showSnackBar('Your reports have been downloaded successfully', 'success');
          }, 500);
        }, 2000);
      }
    }
  };

  const handleDownloadPDF = async () => {
    if (loading) return;

    try {
      setLoading(true);
      showSnackBar(
        'Preparing your reports. Download will start once ready, you can continue working.',
        'info',
        { persistent: true }
      );
      await handlePointOfInterestPdf();
    } catch (error) {
      showSnackBar('Download failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <CircularProgress
          sx={{ color: abyssColors.primary[500] }}
          size={16}
          data-testId="generate-pdf-loading"
        />
      ) : (
          <PictureAsPdf
            data-testid="export-report-icon"
            htmlColor={abyssColors.primary[500]}
            sx={{ cursor: 'pointer' }}
            onClick={handleDownloadPDF}
          />
      )}
    </>
  );
};
